import React from 'react';

// Cigar shape SVG component - renders different vitola shapes
interface CigarShapeProps {
  type: 'robusto' | 'churchill' | 'torpedo' | 'lancero' | 'corona' | 'toro' | 'gordo' | 'petitCorona';
  width?: number;
  className?: string;
}

export const CigarShape: React.FC<CigarShapeProps> = ({ type, width = 120, className = '' }) => {
  const shapes: Record<string, { viewBox: string; path: string; bandX: number; label: string }> = {
    petitCorona: {
      viewBox: '0 0 100 24',
      path: 'M8 12 Q8 6, 14 6 L86 6 Q92 6, 94 10 L96 12 L94 14 Q92 18, 86 18 L14 18 Q8 18, 8 12 Z',
      bandX: 75,
      label: 'Petit Corona',
    },
    robusto: {
      viewBox: '0 0 110 28',
      path: 'M8 14 Q8 6, 16 6 L94 6 Q102 6, 104 12 L106 14 L104 16 Q102 22, 94 22 L16 22 Q8 22, 8 14 Z',
      bandX: 85,
      label: 'Robusto',
    },
    corona: {
      viewBox: '0 0 120 24',
      path: 'M8 12 Q8 5, 15 5 L105 5 Q112 5, 114 10 L116 12 L114 14 Q112 19, 105 19 L15 19 Q8 19, 8 12 Z',
      bandX: 95,
      label: 'Corona',
    },
    toro: {
      viewBox: '0 0 130 28',
      path: 'M8 14 Q8 6, 16 6 L114 6 Q122 6, 124 12 L126 14 L124 16 Q122 22, 114 22 L16 22 Q8 22, 8 14 Z',
      bandX: 105,
      label: 'Toro',
    },
    churchill: {
      viewBox: '0 0 150 26',
      path: 'M8 13 Q8 5, 16 5 L134 5 Q142 5, 144 11 L146 13 L144 15 Q142 21, 134 21 L16 21 Q8 21, 8 13 Z',
      bandX: 125,
      label: 'Churchill',
    },
    lancero: {
      viewBox: '0 0 160 18',
      path: 'M8 9 Q8 4, 14 4 L146 4 Q152 4, 154 8 L156 9 L154 10 Q152 14, 146 14 L14 14 Q8 14, 8 9 Z',
      bandX: 135,
      label: 'Lancero',
    },
    gordo: {
      viewBox: '0 0 130 34',
      path: 'M8 17 Q8 6, 18 6 L112 6 Q122 6, 124 14 L126 17 L124 20 Q122 28, 112 28 L18 28 Q8 28, 8 17 Z',
      bandX: 100,
      label: 'Gordo',
    },
    torpedo: {
      viewBox: '0 0 130 28',
      path: 'M8 14 Q8 6, 16 6 L100 6 Q108 6, 114 8 L124 14 L114 20 Q108 22, 100 22 L16 22 Q8 22, 8 14 Z',
      bandX: 85,
      label: 'Torpedo',
    },
  };

  const shape = shapes[type] || shapes.robusto;
  const height = width * (parseInt(shape.viewBox.split(' ')[3]) / parseInt(shape.viewBox.split(' ')[2]));

  return (
    <svg
      width={width}
      height={height}
      viewBox={shape.viewBox}
      className={className}
      aria-label={shape.label}
    >
      {/* Cigar body gradient */}
      <defs>
        <linearGradient id={`cigar-grad-${type}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#C4956A" />
          <stop offset="30%" stopColor="#A67B5B" />
          <stop offset="70%" stopColor="#8B6544" />
          <stop offset="100%" stopColor="#6B4423" />
        </linearGradient>
        <linearGradient id={`band-grad-${type}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#FFD700" />
          <stop offset="50%" stopColor="#DAA520" />
          <stop offset="100%" stopColor="#B8860B" />
        </linearGradient>
        <linearGradient id={`foot-grad-${type}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#5C4033" />
          <stop offset="100%" stopColor="#3D2817" />
        </linearGradient>
      </defs>

      {/* Cigar body */}
      <path d={shape.path} fill={`url(#cigar-grad-${type})`} />

      {/* Foot (burning end) */}
      <ellipse cx="8" cy={parseInt(shape.viewBox.split(' ')[3]) / 2} rx="3" ry={parseInt(shape.viewBox.split(' ')[3]) / 4} fill={`url(#foot-grad-${type})`} />

      {/* Band */}
      <rect
        x={shape.bandX - 8}
        y={parseInt(shape.viewBox.split(' ')[3]) / 2 - 6}
        width="12"
        height="12"
        rx="1"
        fill={`url(#band-grad-${type})`}
      />
      <rect
        x={shape.bandX - 6}
        y={parseInt(shape.viewBox.split(' ')[3]) / 2 - 4}
        width="8"
        height="8"
        rx="1"
        fill="#8B0000"
      />
    </svg>
  );
};

// Vitola comparison chart showing all shapes - responsive for different screen sizes
export const VitolaChart: React.FC<{ className?: string }> = ({ className = '' }) => {
  const vitolas = [
    { type: 'petitCorona' as const, name: 'Petit Corona', dims: '4.5" × 42', time: '20-30 min', desc: 'Quick smoke' },
    { type: 'corona' as const, name: 'Corona', dims: '5.5" × 42', time: '30-45 min', desc: 'Classic shape' },
    { type: 'robusto' as const, name: 'Robusto', dims: '5" × 50', time: '45-60 min', desc: 'Most popular' },
    { type: 'toro' as const, name: 'Toro', dims: '6" × 50', time: '60-75 min', desc: 'Extended robusto' },
    { type: 'churchill' as const, name: 'Churchill', dims: '7" × 48', time: '90-120 min', desc: 'Grand format' },
    { type: 'lancero' as const, name: 'Lancero', dims: '7.5" × 38', time: '60-90 min', desc: 'Wrapper-forward' },
    { type: 'gordo' as const, name: 'Gordo', dims: '6" × 60', time: '75-90 min', desc: 'Cool, thick smoke' },
    { type: 'torpedo' as const, name: 'Torpedo', dims: '6" × 52', time: '60-75 min', desc: 'Tapered head' },
  ];

  return (
    <div className={`space-y-2 sm:space-y-3 ${className}`}>
      {vitolas.map((v) => (
        <div key={v.type} className="flex items-center gap-2 sm:gap-4 p-2 sm:p-3 bg-white/5 rounded-lg">
          {/* Cigar shape - responsive width */}
          <div className="flex-shrink-0 w-24 sm:w-32 md:w-40 lg:w-48">
            <CigarShape type={v.type} width={180} className="w-full h-auto" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-white text-sm sm:text-base">{v.name}</div>
            <div className="text-xs text-stone-500">{v.dims}</div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-xs sm:text-sm text-amber-500">{v.time}</div>
            <div className="text-xs text-stone-500 hidden sm:block">{v.desc}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Cigar anatomy diagram
export const CigarAnatomyDiagram: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <div className={`relative ${className}`}>
      <svg viewBox="0 0 400 180" className="w-full max-w-md mx-auto">
        <defs>
          {/* Wrapper gradient - outermost */}
          <linearGradient id="wrapper-grad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#C4956A" />
            <stop offset="50%" stopColor="#A67B5B" />
            <stop offset="100%" stopColor="#8B6544" />
          </linearGradient>
          {/* Binder gradient - middle */}
          <linearGradient id="binder-grad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#8B7355" />
            <stop offset="100%" stopColor="#6B5344" />
          </linearGradient>
          {/* Filler gradient - core */}
          <linearGradient id="filler-grad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#5C4033" />
            <stop offset="100%" stopColor="#3D2817" />
          </linearGradient>
          {/* Band */}
          <linearGradient id="anat-band-grad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#FFD700" />
            <stop offset="50%" stopColor="#DAA520" />
            <stop offset="100%" stopColor="#B8860B" />
          </linearGradient>
        </defs>

        {/* Cross-section circle showing layers */}
        <g transform="translate(80, 90)">
          {/* Wrapper - outer ring */}
          <circle cx="0" cy="0" r="45" fill="url(#wrapper-grad)" />
          {/* Binder - middle ring */}
          <circle cx="0" cy="0" r="38" fill="url(#binder-grad)" />
          {/* Filler - core */}
          <circle cx="0" cy="0" r="30" fill="url(#filler-grad)" />

          {/* Labels */}
          <text x="0" y="5" textAnchor="middle" fill="#FFF" fontSize="10" fontWeight="bold">FILLER</text>
        </g>

        {/* Full cigar side view */}
        <g transform="translate(180, 70)">
          {/* Cigar body */}
          <path
            d="M0 20 Q0 8, 12 8 L180 8 Q192 8, 196 16 L200 20 L196 24 Q192 32, 180 32 L12 32 Q0 32, 0 20 Z"
            fill="url(#wrapper-grad)"
          />
          {/* Foot */}
          <ellipse cx="3" cy="20" rx="4" ry="10" fill="url(#filler-grad)" />
          {/* Band */}
          <rect x="155" y="10" width="18" height="20" rx="2" fill="url(#anat-band-grad)" />
          <rect x="158" y="13" width="12" height="14" rx="1" fill="#8B0000" />

          {/* Cap/Head detail */}
          <path d="M192 12 Q198 12, 198 20 Q198 28, 192 28" fill="none" stroke="#6B4423" strokeWidth="1" />
        </g>

        {/* Label lines and text */}
        {/* Wrapper label */}
        <line x1="125" y1="50" x2="200" y2="50" stroke="#F59E0B" strokeWidth="1" strokeDasharray="3,3" />
        <circle cx="125" cy="50" r="3" fill="#F59E0B" />
        <text x="205" y="54" fill="#F59E0B" fontSize="11" fontWeight="bold">WRAPPER</text>
        <text x="205" y="66" fill="#A8A29E" fontSize="9">The outer leaf (60-90% of flavor)</text>

        {/* Binder label */}
        <line x1="110" y1="90" x2="200" y2="95" stroke="#78716C" strokeWidth="1" strokeDasharray="3,3" />
        <circle cx="110" cy="90" r="3" fill="#78716C" />
        <text x="205" y="99" fill="#78716C" fontSize="11" fontWeight="bold">BINDER</text>
        <text x="205" y="111" fill="#A8A29E" fontSize="9">Holds filler together</text>

        {/* Filler label */}
        <line x1="80" y1="115" x2="200" y2="135" stroke="#57534E" strokeWidth="1" strokeDasharray="3,3" />
        <circle cx="80" cy="115" r="3" fill="#57534E" />
        <text x="205" y="139" fill="#57534E" fontSize="11" fontWeight="bold">FILLER</text>
        <text x="205" y="151" fill="#A8A29E" fontSize="9">The core blend (body & strength)</text>

        {/* Parts labels on cigar */}
        <text x="185" y="55" fill="#FFF" fontSize="9" textAnchor="middle">HEAD</text>
        <text x="270" y="55" fill="#DAA520" fontSize="9" textAnchor="middle">BAND</text>
        <text x="330" y="55" fill="#FFF" fontSize="9" textAnchor="middle">BODY</text>
        <text x="185" y="115" fill="#78716C" fontSize="9" textAnchor="middle">FOOT</text>
      </svg>
    </div>
  );
};

// Wrapper color swatches
interface WrapperSwatchProps {
  className?: string;
}

export const WrapperSwatches: React.FC<WrapperSwatchProps> = ({ className = '' }) => {
  const wrappers = [
    { name: 'Double Claro', shortName: 'D. Claro', color: '#E8DCC8', textColor: '#333', strength: 'Very Mild', aka: 'Candela/Green' },
    { name: 'Claro', shortName: 'Claro', color: '#D4B896', textColor: '#333', strength: 'Mild', aka: 'Natural/Light' },
    { name: 'Colorado Claro', shortName: 'Col. Claro', color: '#C4956A', textColor: '#FFF', strength: 'Mild-Med', aka: 'Connecticut' },
    { name: 'Colorado', shortName: 'Colorado', color: '#A67B5B', textColor: '#FFF', strength: 'Medium', aka: 'Rosado' },
    { name: 'Colorado Maduro', shortName: 'Col. Mad.', color: '#6B4423', textColor: '#FFF', strength: 'Med-Full', aka: 'Dark' },
    { name: 'Maduro', shortName: 'Maduro', color: '#3D2817', textColor: '#FFF', strength: 'Full', aka: 'Very Dark' },
    { name: 'Oscuro', shortName: 'Oscuro', color: '#1A0F0A', textColor: '#FFF', strength: 'Very Full', aka: 'Double Maduro' },
  ];

  return (
    <div className={`${className}`}>
      {/* Responsive grid - horizontal on larger screens, wrapping on mobile */}
      <div className="grid grid-cols-4 sm:grid-cols-7 gap-1 sm:gap-2 mb-3">
        {wrappers.map((w) => (
          <div key={w.name} className="text-center">
            <div
              className="h-12 sm:h-16 md:h-20 rounded-t-lg border border-white/10"
              style={{ backgroundColor: w.color }}
            />
            <div className="bg-white/5 rounded-b-lg p-1 sm:p-1.5 border border-t-0 border-white/10">
              {/* Show short name on mobile, full name on larger screens */}
              <div className="text-[9px] sm:text-[10px] md:text-xs font-medium text-white truncate sm:hidden">{w.shortName}</div>
              <div className="text-[10px] md:text-xs font-medium text-white truncate hidden sm:block">{w.name}</div>
              <div className="text-[8px] sm:text-[9px] md:text-xs text-amber-500">{w.strength}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="text-[10px] sm:text-xs text-stone-500 text-center">
        Lighter wrappers = milder flavor | Darker wrappers = richer, sweeter notes
      </div>
    </div>
  );
};

// Ring gauge visual explainer
export const RingGaugeExplainer: React.FC<{ className?: string }> = ({ className = '' }) => {
  const gauges = [
    { size: 38, label: '38', desc: 'Thin' },
    { size: 42, label: '42', desc: 'Classic' },
    { size: 50, label: '50', desc: 'Standard' },
    { size: 54, label: '54', desc: 'Thick' },
    { size: 60, label: '60', desc: 'Gordo' },
  ];

  return (
    <div className={`${className}`}>
      <div className="flex items-end justify-center gap-4 mb-4">
        {gauges.map((g) => {
          const diameter = (g.size / 64) * 40; // Scale for display
          return (
            <div key={g.size} className="flex flex-col items-center">
              <div
                className="rounded-full bg-gradient-to-b from-amber-700 to-amber-900 border-2 border-amber-600"
                style={{ width: diameter, height: diameter }}
              />
              <div className="mt-2 text-center">
                <div className="text-sm font-bold text-white">{g.label}</div>
                <div className="text-xs text-stone-500">{g.desc}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="text-center text-xs text-stone-400">
        Ring gauge = diameter in 64ths of an inch (50 ring = 50/64" = ~0.78")
      </div>
    </div>
  );
};

// Cutting methods illustration
export const CuttingMethodsDiagram: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <div className={`grid grid-cols-3 gap-4 ${className}`}>
      {/* Straight Cut */}
      <div className="text-center">
        <svg viewBox="0 0 60 80" className="w-16 h-20 mx-auto">
          <defs>
            <linearGradient id="cut-cigar" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#C4956A" />
              <stop offset="100%" stopColor="#6B4423" />
            </linearGradient>
          </defs>
          {/* Cigar head */}
          <ellipse cx="30" cy="50" rx="18" ry="25" fill="url(#cut-cigar)" />
          {/* Cut line */}
          <line x1="10" y1="30" x2="50" y2="30" stroke="#F59E0B" strokeWidth="2" />
          {/* Scissors icon */}
          <path d="M25 15 L30 22 L35 15 M28 18 L32 18" stroke="#FFF" strokeWidth="2" fill="none" />
        </svg>
        <div className="mt-2 font-medium text-white text-sm">Straight Cut</div>
        <div className="text-xs text-stone-500">Most common</div>
      </div>

      {/* V-Cut */}
      <div className="text-center">
        <svg viewBox="0 0 60 80" className="w-16 h-20 mx-auto">
          <ellipse cx="30" cy="50" rx="18" ry="25" fill="url(#cut-cigar)" />
          {/* V notch */}
          <path d="M22 30 L30 42 L38 30" stroke="#F59E0B" strokeWidth="2" fill="none" />
        </svg>
        <div className="mt-2 font-medium text-white text-sm">V-Cut</div>
        <div className="text-xs text-stone-500">Concentrated draw</div>
      </div>

      {/* Punch */}
      <div className="text-center">
        <svg viewBox="0 0 60 80" className="w-16 h-20 mx-auto">
          <ellipse cx="30" cy="50" rx="18" ry="25" fill="url(#cut-cigar)" />
          {/* Punch hole */}
          <circle cx="30" cy="35" r="6" fill="#3D2817" stroke="#F59E0B" strokeWidth="2" />
        </svg>
        <div className="mt-2 font-medium text-white text-sm">Punch</div>
        <div className="text-xs text-stone-500">Cool smoke</div>
      </div>
    </div>
  );
};

// Lighting steps illustration
export const LightingStepsDiagram: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center gap-4 p-3 bg-white/5 rounded-lg">
        <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center text-xl">
          1
        </div>
        <div className="flex-1">
          <div className="font-medium text-white">Toast the Foot</div>
          <div className="text-xs text-stone-400">Hold flame 1/2" away, rotate cigar</div>
        </div>
        <svg viewBox="0 0 50 50" className="w-10 h-10">
          <ellipse cx="25" cy="30" rx="12" ry="8" fill="#6B4423" />
          <path d="M20 40 Q25 25, 30 40" fill="#F59E0B" opacity="0.8" />
          <path d="M22 42 Q25 32, 28 42" fill="#EF4444" opacity="0.9" />
        </svg>
      </div>

      <div className="flex items-center gap-4 p-3 bg-white/5 rounded-lg">
        <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center text-xl">
          2
        </div>
        <div className="flex-1">
          <div className="font-medium text-white">Check the Glow</div>
          <div className="text-xs text-stone-400">Foot should glow evenly orange</div>
        </div>
        <svg viewBox="0 0 50 50" className="w-10 h-10">
          <ellipse cx="25" cy="25" rx="15" ry="15" fill="#6B4423" />
          <ellipse cx="25" cy="25" rx="12" ry="12" fill="#F97316" opacity="0.8" />
          <ellipse cx="25" cy="25" rx="8" ry="8" fill="#EF4444" opacity="0.6" />
        </svg>
      </div>

      <div className="flex items-center gap-4 p-3 bg-white/5 rounded-lg">
        <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center text-xl">
          3
        </div>
        <div className="flex-1">
          <div className="font-medium text-white">First Puff</div>
          <div className="text-xs text-stone-400">Gentle draw, touch up any dark spots</div>
        </div>
        <svg viewBox="0 0 50 50" className="w-10 h-10">
          <ellipse cx="20" cy="30" rx="10" ry="6" fill="#6B4423" />
          <ellipse cx="20" cy="30" rx="7" ry="4" fill="#F97316" />
          {/* Smoke wisps */}
          <path d="M30 28 Q35 20, 32 12" stroke="#A8A29E" strokeWidth="2" fill="none" opacity="0.5" />
          <path d="M35 30 Q40 22, 38 15" stroke="#A8A29E" strokeWidth="2" fill="none" opacity="0.3" />
        </svg>
      </div>
    </div>
  );
};
